import { useEffect, useRef, useState } from 'react';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useScreenSize from '@/hooks/useScreenSize';
import useStateHandler from '@/hooks/useStateHandler';
import ProfileThoughtCard from '@/shared-components/cards/components/ProfileThoughtCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

import './styles/VirtualList.css';

const THOUGHT_CARD_LAYOUT_SIGNATURE = 'profile-thought-card-v2';

export default function VirtualList({ items, visibleItems, setItems, onLoadMore, cacheKey }) {
  const { cacheHandler } = useStateHandler();
  const screenSize = useScreenSize();
  const canReuseCachedHeights =
    items.cache?.cacheValidFor === screenSize.width &&
    items.cache?.layoutSignature === THOUGHT_CARD_LAYOUT_SIGNATURE;
  const heightsCache = useRef({
    cacheValidFor: screenSize.width,
    layoutSignature: THOUGHT_CARD_LAYOUT_SIGNATURE,
    heights: canReuseCachedHeights ? [...items.cache.heights] : [],
  });
  const [shouldLoadItemsOnRenderPhase, setShouldLoadItemsOnRenderPhase] = useState(heightsCache.current.heights.length > 0);
  const rowMeasurerRef = useRef(document.getElementById("activity-thought-card-measurer"));
  const listWidthRef = useRef(0);
  const measurerWidthRef = useRef(0);
  const isFirstRender = useIsFirstRender();
  const listRef = useRef();
  const previousItemsLengthRef = useRef(visibleItems.length);

  function getMeasuredTextContainerWidth() {
    if (typeof document === "undefined") {
      return null;
    }

    const scopedContainer = document.querySelector(
      ".activity-liked-thoughts__virtual-list .profile-thought-card__thought-text-container"
    );

    if (scopedContainer) {
      const { width } = scopedContainer.getBoundingClientRect();

      if (width && width > 1) {
        return width;
      }
    }

    return null;
  }

  useEffect(() => {
    const previousLength = previousItemsLengthRef.current;
    const currentLength = visibleItems.length;
    if(currentLength !== previousLength) {
      if(currentLength > previousLength && previousLength > 0) {
        const previousLastIndex = previousLength - 1;
        heightsCache.current.heights[previousLastIndex] = null;
        const scheduler = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
        scheduler(() => {
          if(listRef.current) {
            listRef.current.recomputeRowHeights();
          }
        });
      } else if(currentLength < previousLength) {
        heightsCache.current = {
          cacheValidFor: screenSize.width,
          layoutSignature: THOUGHT_CARD_LAYOUT_SIGNATURE,
          heights: [],
        };
      }
      previousItemsLengthRef.current = currentLength;
    }
  }, [visibleItems.length]);

  function resolveListWidthWithFallback(widthCandidate) {
    return widthCandidate && widthCandidate > 1 ? widthCandidate : Math.min(screenSize.width, 800);
  }

  function getTextContainerDeduction() {
    if(screenSize.width <= 550) return 216;
    if(screenSize.width <= 1004) return 261;
    return 310;
  }

  function ensureRowMeasurer(listWidthCandidate) {
    if(!rowMeasurerRef.current) {
      const newRowMeasurer = document.createElement("p");

      newRowMeasurer.id = "activity-thought-card-measurer";
      newRowMeasurer.className = "text-custom__component paragraph profile-thought-card__thought-text-container";
      rowMeasurerRef.current = newRowMeasurer;
      document.body.appendChild(newRowMeasurer);
    }

    const baseWidth = resolveListWidthWithFallback(listWidthCandidate ?? listWidthRef.current);
    const deduction = getTextContainerDeduction();
    const measuredWidth = getMeasuredTextContainerWidth();
    const computedWidth = Math.max(measuredWidth ?? (baseWidth - deduction), 1);
    const previousWidth = measurerWidthRef.current;

    if(previousWidth !== computedWidth) {
      rowMeasurerRef.current.style.width = `${computedWidth}px`;
      measurerWidthRef.current = computedWidth;

      if(measuredWidth && listRef.current) {
        heightsCache.current = {
          cacheValidFor: screenSize.width,
          layoutSignature: THOUGHT_CARD_LAYOUT_SIGNATURE,
          heights: [],
        };

        const scheduler = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => setTimeout(cb, 0);

        scheduler(() => {
          if(listRef.current) {
            listRef.current.recomputeRowHeights();
          }
        });
      }
    }

    return rowMeasurerRef.current;
  }

  function measureRow({ index }) {
    if(!heightsCache.current.heights[index]) {
      const heightOfEmtpyThought = screenSize.width <= 550 ? 72 : screenSize.width <= 1004 ? 105 : 140;
      const heightOfTextContainer = screenSize.width <= 550 ? 36 : screenSize.width <= 1004 ? 40 : 45;
      //            top + bottom paddings of the card for each breakpoint
      const internalPaddingsOfThought =
        screenSize.width <= 550
          ? (20 + 20)
          : screenSize.width <= 1004
            ? (12 + 10)
            : (15 + 15);
      const spacerBase =
        screenSize.width <= 550
          ? 11
          : screenSize.width <= 1004
            ? 14
            : 12;
      const navigationOffset =
        screenSize.width <= 650
          ? (document.querySelector('.app-navigation__container-outter')?.getBoundingClientRect().height || 110)
          : 0;
      const spacer = index === visibleItems.length - 1 ? spacerBase + navigationOffset : spacerBase;

      ensureRowMeasurer();
            
      const thoughtText = visibleItems[index].thought_message;

      rowMeasurerRef.current.innerHTML = thoughtText.length <= 400 ? thoughtText : `${thoughtText.slice(0, 400)}...<span> more</span>`;

      const internalHeight = Math.max(heightOfEmtpyThought, heightOfTextContainer + rowMeasurerRef.current.offsetHeight);

      rowMeasurerRef.current.innerHTML = "";

      heightsCache.current.heights[index] = internalHeight + spacer + internalPaddingsOfThought;
      return internalHeight + spacer + internalPaddingsOfThought;
    } else {
      return heightsCache.current.heights[index];
    }
  }

  function onLikeAction(likeStatus, thoughtId) {
    setItems({
      ...items,
      data: items.data.map(thought => {
        if(thought._id === thoughtId) {
          return {
            ...thought,
            thought_likes_count: likeStatus ? thought.thought_likes_count + 1 : thought.thought_likes_count - 1
          };
        } else {
          return thought;
        }
      })
    });
  }

  useEffect(()=>{
    if(!isFirstRender) {
      window.scrollTo(0,0);

      setTimeout(()=>{
        heightsCache.current = {
          cacheValidFor: screenSize.width,
          layoutSignature: THOUGHT_CARD_LAYOUT_SIGNATURE,
          heights: [],
        };
        ensureRowMeasurer(listWidthRef.current);
        listRef.current.recomputeRowHeights();
      }, 50);
    }
  }, [screenSize.width]);

  useEffect(()=>{
    if(!shouldLoadItemsOnRenderPhase) {
      setShouldLoadItemsOnRenderPhase(true);
    }

    return () => {
      const cacheValue = cacheHandler.getCacheValue(cacheKey);

      if(cacheValue) {
        setItems({
          ...cacheValue.data,
          cache: {
            ...heightsCache.current,
            layoutSignature: THOUGHT_CARD_LAYOUT_SIGNATURE,
          },
        });
      }
    };
  }, []);

  if(!shouldLoadItemsOnRenderPhase) {
    return (
      <></>
    );
  } else {
    return (
      <>
        <InfiniteScroll 
          onLoadMore={onLoadMore} 
          shouldLoadMore={items.data.length < items.totalDocs} 
          offsetElements={3} 
          listLength={visibleItems.length} 
          className="profile-profile-thoughts__thoughts-infinite-scroll activity-liked-thoughts__virtual-list"
        >
          {({ infiniteScrollAssigner }) => (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <AutoSizer disableHeight>
                  {({ width }) => {
                    const resolvedWidth = resolveListWidthWithFallback(width === 0 ? listWidthRef.current : width);

                    listWidthRef.current = resolvedWidth;
                    ensureRowMeasurer(resolvedWidth);

                    return (
                      <List
                        autoHeight
                        width={resolvedWidth === 0 ? 0.1 : resolvedWidth}
                        height={height}
                        rowCount={visibleItems.length}
                        rowHeight={measureRow}
                        overscanRowCount={5}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        scrollTop={scrollTop}
                        estimatedRowSize={300}
                        ref={listRef}
                        rowRenderer={({ index, key, style }) => (
                          <div
                            key={key}
                            style={{ ...style, overflow: 'visible' }}
                            ref={infiniteScrollAssigner(index)}
                          >
                            <ProfileThoughtCard 
                              data={visibleItems[index]} 
                              onLikeAction={(likeStatus)=>onLikeAction(likeStatus, visibleItems[index]._id)}
                            />
                          </div>
                        )}
                      />
                    );
                  }}
                </AutoSizer>
              )}
            </WindowScroller>
          )}
        </InfiniteScroll>
      </>
    );
  }
}
